import React, {
  createContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// third-party
import { jwtDecode } from "jwt-decode";

// reducer - state management
import { LOGIN, LOGOUT } from "contexts/auth-reducer/actions";
import authReducer from "contexts/auth-reducer/auth";

// project imports
import Loader from "components/Loader";
import axios from "utils/axios";
import { KeyedObject } from "types/root";
import { AuthProps, JWTContextType, RegisterOptions } from "types/auth";
import {
  listAirports,
  loginUser,
  registerUser,
  type AirportRecord,
  type AuthUserRecord,
} from "api/auth";

const TOKEN_STORAGE_KEY = "serviceToken";
const USER_STORAGE_KEY = "serviceUser";
const ADMIN_AIRPORT_STORAGE_KEY = "serviceAdminAirportSelection";
const WEB_APP_ALLOWED_POSITIONS = new Set([
  "admin",
  "manager",
  "lead_supervisor",
]);
const WEB_APP_ACCESS_ERROR =
  "Web app access is restricted to admin, lead_supervisor, and manager roles.";

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

type AdminAirportSelection = {
  userId: string;
  airportId: string;
  airportName?: string | null;
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) return false;
  const decoded: KeyedObject = jwtDecode(serviceToken);
  return Boolean(decoded.exp && decoded.exp > Date.now() / 1000);
};

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem(TOKEN_STORAGE_KEY, serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    delete axios.defaults.headers.common.Authorization;
  }
};

const setStoredUser = (user?: KeyedObject | null) => {
  if (!user) {
    localStorage.removeItem(USER_STORAGE_KEY);
    return;
  }
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
};

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setStoredAdminAirportSelection = (
  selection?: AdminAirportSelection | null,
) => {
  if (!selection?.airportId || !selection.userId) {
    localStorage.removeItem(ADMIN_AIRPORT_STORAGE_KEY);
    return;
  }
  localStorage.setItem(ADMIN_AIRPORT_STORAGE_KEY, JSON.stringify(selection));
};

const readStoredAdminAirportSelection = () => {
  try {
    const raw = localStorage.getItem(ADMIN_AIRPORT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AdminAirportSelection) : null;
  } catch {
    return null;
  }
};

function getPositionInfo(position: AuthUserRecord["position"]) {
  if (!position) return { positionKey: undefined, positionLabel: undefined };
  if (typeof position === "string")
    return { positionKey: position, positionLabel: position };
  return {
    positionKey: position.key,
    positionLabel: position.label || position.key,
  };
}

function getAirportInfo(airport: AuthUserRecord["airport"]) {
  if (!airport) return { airportId: null, airportName: null };
  if (typeof airport === "string")
    return { airportId: airport, airportName: airport };
  return {
    airportId: airport._id,
    airportName: airport.name || airport.code || airport._id,
  };
}

function normalizeUser(user: AuthUserRecord) {
  const { positionKey, positionLabel } = getPositionInfo(user.position);
  const { airportId, airportName } = getAirportInfo(user.airport);
  const preferredName = user.preferredName || user.firstName || "";
  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    id: user._id,
    firstName: user.firstName,
    lastName: user.lastName,
    preferredName,
    employee_id: user.employee_id,
    name: preferredName || fullName || user.employee_id || user.email || "User",
    email: user.email || undefined,
    role: positionLabel || positionKey,
    positionKey,
    positionLabel,
    airportId,
    airportName,
  };
}

function hasWebAppAccess(positionKey: unknown) {
  return WEB_APP_ALLOWED_POSITIONS.has(String(positionKey || "").toLowerCase());
}

function isAdminPosition(positionKey: unknown) {
  return String(positionKey || "")
    .trim()
    .toLowerCase()
    .includes("admin");
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [adminAirports, setAdminAirports] = useState<AirportRecord[]>([]);
  const [adminAirportLoading, setAdminAirportLoading] = useState(false);
  const [adminAirportError, setAdminAirportError] = useState<string | null>(
    null,
  );
  const [adminAirportSelection, setAdminAirportSelection] =
    useState<AdminAirportSelection | null>(null);
  const [adminAirportDraftId, setAdminAirportDraftId] = useState("");

  const isAdminUser = useMemo(
    () => isAdminPosition(state.user?.positionKey),
    [state.user?.positionKey],
  );
  const requiresAdminAirportSelection = Boolean(
    state.isLoggedIn && isAdminUser,
  );

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
        const storedUser = readStoredUser();

        if (serviceToken && verifyToken(serviceToken) && storedUser) {
          if (!hasWebAppAccess(storedUser?.positionKey)) {
            setSession(null);
            setStoredUser(null);
            setStoredAdminAirportSelection(null);
            dispatch({ type: LOGOUT });
            return;
          }

          const storedAdminAirport = readStoredAdminAirportSelection();
          if (
            isAdminPosition(storedUser?.positionKey) &&
            storedAdminAirport?.userId === storedUser?.id &&
            storedAdminAirport?.airportId
          ) {
            setAdminAirportSelection(storedAdminAirport);
            setAdminAirportDraftId(storedAdminAirport.airportId);
          } else {
            setStoredAdminAirportSelection(null);
            setAdminAirportSelection(null);
            setAdminAirportDraftId("");
          }

          setSession(serviceToken);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: storedUser,
            },
          });
          return;
        }
      } catch (err) {
        console.error(err);
      }

      setSession(null);
      setStoredUser(null);
      setStoredAdminAirportSelection(null);
      dispatch({ type: LOGOUT });
    };

    void init();
  }, []);

  useEffect(() => {
    if (!requiresAdminAirportSelection) {
      setAdminAirports([]);
      setAdminAirportLoading(false);
      setAdminAirportError(null);
      setAdminAirportSelection(null);
      setAdminAirportDraftId("");
      return;
    }

    let mounted = true;
    const run = async () => {
      setAdminAirportLoading(true);
      setAdminAirportError(null);
      try {
        const rows = await listAirports();
        if (!mounted) return;

        const sortedRows = [...rows]
          .filter((airport) => airport.isActive !== false)
          .sort((left, right) =>
            `${left.code || ""} ${left.name || ""}`.localeCompare(
              `${right.code || ""} ${right.name || ""}`,
            ),
          );

        setAdminAirports(sortedRows);
        setAdminAirportDraftId((previous) => {
          if (
            previous &&
            sortedRows.some((airport) => airport._id === previous)
          ) {
            return previous;
          }
          if (
            adminAirportSelection?.airportId &&
            sortedRows.some(
              (airport) => airport._id === adminAirportSelection.airportId,
            )
          ) {
            return adminAirportSelection.airportId;
          }
          return "";
        });
      } catch (error) {
        if (!mounted) return;
        setAdminAirports([]);
        setAdminAirportError(
          error instanceof Error ? error.message : "Failed to load airports.",
        );
      } finally {
        if (mounted) setAdminAirportLoading(false);
      }
    };

    void run();

    return () => {
      mounted = false;
    };
  }, [adminAirportSelection?.airportId, requiresAdminAirportSelection]);

  const login = async (employeeId: string, password: string) => {
    const response = await loginUser({ employee_id: employeeId, password });
    const serviceToken = response.token;
    const user = normalizeUser(response.user);
    if (!hasWebAppAccess(user.positionKey)) {
      setSession(null);
      setStoredUser(null);
      dispatch({ type: LOGOUT });
      throw new Error(WEB_APP_ACCESS_ERROR);
    }

    setSession(serviceToken);
    setStoredUser(user);
    setStoredAdminAirportSelection(null);
    setAdminAirportSelection(null);
    setAdminAirportDraftId("");

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user,
      },
    });
  };

  const register = async (
    employeeId: string,
    password: string,
    firstName: string,
    lastName: string,
    options?: RegisterOptions,
  ) => {
    await registerUser({
      firstName,
      lastName,
      preferredName: options?.preferredName || firstName,
      employee_id: employeeId,
      email: options?.email,
      password,
      position: options?.position,
      airportId: options?.airportId,
    });
  };

  const logout = () => {
    setSession(null);
    setStoredUser(null);
    setStoredAdminAirportSelection(null);
    setAdminAirportSelection(null);
    setAdminAirportDraftId("");
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async () => {
    // The provided API set currently includes login/register only for auth flows.
    // Keep this method non-breaking for existing UI paths.
    return Promise.resolve();
  };

  const updateProfile = () => {
    const storedUser = readStoredUser();
    if (!storedUser) return;

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: storedUser,
      },
    });
  };

  const effectiveUser = useMemo(() => {
    if (!state.user) return state.user;
    if (!isAdminPosition(state.user.positionKey)) return state.user;
    if (!adminAirportSelection?.airportId) {
      return {
        ...state.user,
        airportId: null,
        airportName: null,
      };
    }

    const matchedAirport = adminAirports.find(
      (airport) => airport._id === adminAirportSelection.airportId,
    );

    return {
      ...state.user,
      airportId: adminAirportSelection.airportId,
      airportName:
        matchedAirport?.name ||
        adminAirportSelection.airportName ||
        adminAirportSelection.airportId,
    };
  }, [adminAirportSelection, adminAirports, state.user]);

  const adminAirportModalOpen =
    requiresAdminAirportSelection && !adminAirportSelection?.airportId;

  const handleConfirmAdminAirport = () => {
    const selectedAirport = adminAirports.find(
      (airport) => airport._id === adminAirportDraftId,
    );
    if (!state.user?.id || !selectedAirport) return;

    const nextSelection = {
      userId: state.user.id,
      airportId: selectedAirport._id,
      airportName:
        selectedAirport.name || selectedAirport.code || selectedAirport._id,
    };

    setAdminAirportSelection(nextSelection);
    setStoredAdminAirportSelection(nextSelection);
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext
      value={{
        ...state,
        user: effectiveUser,
        login,
        logout,
        register,
        resetPassword,
        updateProfile,
      }}
    >
      <>
        {!adminAirportModalOpen ? children : null}
        <Dialog
          open={adminAirportModalOpen}
          fullWidth
          maxWidth="xs"
          disableEscapeKeyDown
        >
          <DialogTitle>Select Airport</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {adminAirportLoading ? <LinearProgress /> : null}
              <Typography variant="body2" color="text.secondary">
                Choose the airport for this admin session. Pages that require an
                airport id will use this selection.
              </Typography>
              {adminAirportError ? (
                <Alert severity="error">{adminAirportError}</Alert>
              ) : null}
              <TextField
                select
                fullWidth
                label="Airport"
                value={adminAirportDraftId}
                onChange={(event) => setAdminAirportDraftId(event.target.value)}
                disabled={adminAirportLoading || adminAirports.length === 0}
              >
                <MenuItem value="" disabled>
                  Select airport
                </MenuItem>
                {adminAirports.map((airport) => (
                  <MenuItem key={airport._id} value={airport._id}>
                    {airport.code} - {airport.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAdminAirportError(null);
                setAdminAirports([]);
                setAdminAirportLoading(true);
                void listAirports()
                  .then((rows) => {
                    const sortedRows = [...rows]
                      .filter((airport) => airport.isActive !== false)
                      .sort((left, right) =>
                        `${left.code || ""} ${left.name || ""}`.localeCompare(
                          `${right.code || ""} ${right.name || ""}`,
                        ),
                      );
                    setAdminAirports(sortedRows);
                  })
                  .catch((error) => {
                    setAdminAirportError(
                      error instanceof Error
                        ? error.message
                        : "Failed to load airports.",
                    );
                  })
                  .finally(() => {
                    setAdminAirportLoading(false);
                  });
              }}
              disabled={adminAirportLoading}
            >
              Reload
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAdminAirport}
              disabled={!adminAirportDraftId || adminAirportLoading}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </JWTContext>
  );
};

export default JWTContext;
